/**
 * FloorPanel — Dev tool for swapping floor textures and adjusting properties.
 * Opens as a separate popup from the LightingPanel.
 */

import React, { useState, useCallback } from 'react';
import * as THREE from 'three';
import { LightingRegistryEntry } from './lightingRegistry';

const FLOOR_TEXTURES = [
  { label: 'Procedural Concrete', path: null },
  { label: 'Concrete Bare', path: '/textures/floor-concrete-bare.jpg' },
  { label: 'Concrete Dirty', path: '/textures/floor-concrete-dirty.jpg' },
  { label: 'Wood Dark Parquet', path: '/textures/floor-wood-dark.jpg' },
  { label: 'Wood Light Parquet', path: '/textures/floor-wood-light.jpg' },
  { label: 'Wood Medium Parquet', path: '/textures/floor-wood-medium.jpg' },
];

interface FloorPanelProps {
  entry: LightingRegistryEntry;
  onClose: () => void;
}

export const FloorPanel: React.FC<FloorPanelProps> = ({ entry, onClose }) => {
  const [selectedIdx, setSelectedIdx] = useState(2); // Concrete Dirty
  const [repeatX, setRepeatX] = useState(3);
  const [repeatY, setRepeatY] = useState(3);
  const [roughness, setRoughness] = useState(1);
  const [metalness, setMetalness] = useState(0);
  const [color, setColor] = useState('#ffffff');
  const [, forceUpdate] = useState(0);

  const floorMat = entry.floorMesh?.material as THREE.MeshStandardMaterial | undefined;

  const render = useCallback(() => {
    entry.renderScene();
  }, [entry]);

  const applyTexture = useCallback((idx: number) => {
    if (!floorMat) return;
    setSelectedIdx(idx);
    const tex = FLOOR_TEXTURES[idx];

    if (!tex.path) {
      // Procedural — remove map, just use color
      if (floorMat.map) {
        floorMat.map.dispose();
        floorMat.map = null;
      }
      floorMat.needsUpdate = true;
      render();
      return;
    }

    const loader = new THREE.TextureLoader();
    loader.load(tex.path, (texture) => {
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(repeatX, repeatY);
      texture.colorSpace = THREE.SRGBColorSpace;

      if (floorMat.map) floorMat.map.dispose();
      floorMat.map = texture;
      floorMat.needsUpdate = true;
      render();
    });
  }, [floorMat, repeatX, repeatY, render]);

  const handleRepeatX = useCallback((v: number) => {
    setRepeatX(v);
    if (floorMat?.map) {
      floorMat.map.repeat.setX(v);
      render();
    }
  }, [floorMat, render]);

  const handleRepeatY = useCallback((v: number) => {
    setRepeatY(v);
    if (floorMat?.map) {
      floorMat.map.repeat.setY(v);
      render();
    }
  }, [floorMat, render]);

  const handleRoughness = useCallback((v: number) => {
    setRoughness(v);
    if (floorMat) { floorMat.roughness = v; render(); }
  }, [floorMat, render]);

  const handleMetalness = useCallback((v: number) => {
    setMetalness(v);
    if (floorMat) { floorMat.metalness = v; render(); }
  }, [floorMat, render]);

  const handleColor = useCallback((hex: string) => {
    setColor(hex);
    if (floorMat) { floorMat.color.set(hex); render(); }
  }, [floorMat, render]);

  const handleRotation = useCallback((deg: number) => {
    if (floorMat?.map) {
      floorMat.map.rotation = (deg * Math.PI) / 180;
      floorMat.map.center.set(0.5, 0.5);
      render();
    }
  }, [floorMat, render]);

  const copySettings = useCallback(() => {
    const settings = {
      texture: FLOOR_TEXTURES[selectedIdx].label,
      path: FLOOR_TEXTURES[selectedIdx].path,
      repeat: { x: repeatX, y: repeatY },
      rotation: floorMat?.map ? +(floorMat.map.rotation * 180 / Math.PI).toFixed(0) : 0,
      roughness,
      metalness,
      color,
    };
    navigator.clipboard.writeText(JSON.stringify(settings, null, 2));
    console.log('%c[FloorPanel] Settings copied', 'color:#a6e3a1');
  }, [selectedIdx, repeatX, repeatY, roughness, metalness, color, floorMat]);

  const S: React.CSSProperties = {
    position: 'fixed',
    top: '20px',
    left: '340px',
    zIndex: 99997,
    width: '280px',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#1e1e2e',
    border: '1px solid #3b3b52',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#cdd6f4',
    animation: 'devtools-slide-in 0.2s ease-out',
  };

  const sliderRow = (label: string, value: number, min: number, max: number, step: number, onChange: (v: number) => void, accent = '#89b4fa') => (
    <div style={{ marginBottom: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace', color: '#89b4fa' }}>{value % 1 === 0 ? value : value.toFixed(2)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: accent, height: '16px' }} />
    </div>
  );

  return (
    <div style={S}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', background: '#181825', borderBottom: '1px solid #3b3b52',
        borderRadius: '12px 12px 0 0',
      }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>🏗️ Floor Textures</span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: '#6c7086', fontSize: '16px',
          cursor: 'pointer', padding: '2px 6px', borderRadius: '4px',
        }}>✕</button>
      </div>

      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {/* Texture selector */}
        <div>
          <div style={{ fontSize: '11px', color: '#a6adc8', marginBottom: '4px' }}>Texture</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {FLOOR_TEXTURES.map((tex, idx) => (
              <button key={tex.label} onClick={() => applyTexture(idx)} style={{
                padding: '5px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
                cursor: 'pointer', textAlign: 'left',
                border: selectedIdx === idx ? '1px solid #a6e3a1' : '1px solid #3b3b52',
                background: selectedIdx === idx ? '#313244' : '#242438',
                color: selectedIdx === idx ? '#a6e3a1' : '#cdd6f4',
              }}>{tex.label}</button>
            ))}
          </div>
        </div>

        {/* Tiling */}
        <div style={{ borderTop: '1px solid #3b3b52', paddingTop: '8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#a6e3a1', marginBottom: '6px' }}>Tiling &amp; Transform</div>
          {sliderRow('Repeat X', repeatX, 0.5, 20, 0.5, handleRepeatX, '#a6e3a1')}
          {sliderRow('Repeat Y', repeatY, 0.5, 20, 0.5, handleRepeatY, '#a6e3a1')}
          {sliderRow('Rotation (deg)', floorMat?.map ? +(floorMat.map.rotation * 180 / Math.PI).toFixed(0) : 0, 0, 360, 15, handleRotation, '#a6e3a1')}
        </div>

        {/* Material properties */}
        <div style={{ borderTop: '1px solid #3b3b52', paddingTop: '8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#fab387', marginBottom: '6px' }}>Material</div>
          {sliderRow('Roughness', roughness, 0, 1, 0.02, handleRoughness, '#fab387')}
          {sliderRow('Metalness', metalness, 0, 1, 0.02, handleMetalness, '#fab387')}
          <div style={{ marginBottom: '6px' }}>
            <div style={{ fontSize: '11px', color: '#a6adc8', marginBottom: '2px' }}>Tint Color</div>
            <input type="color" value={color} onChange={e => handleColor(e.target.value)}
              style={{ width: '100%', height: '24px', border: 'none', borderRadius: '4px', cursor: 'pointer' }} />
          </div>
        </div>

        {/* Copy */}
        <button onClick={copySettings} style={{
          padding: '8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
          cursor: 'pointer', border: '1px solid #3b3b52',
          background: '#242438', color: '#a6e3a1', textAlign: 'center',
        }}>📋 Copy Floor Settings</button>
      </div>

      <div style={{
        padding: '6px 14px', fontSize: '10px', color: '#585b70', textAlign: 'center',
        borderTop: '1px solid #3b3b52', background: '#181825', borderRadius: '0 0 12px 12px',
      }}>Textures tile seamlessly on the floor</div>
    </div>
  );
};
